import type {
  SpaceSwitchRestoreMode,
  SpaceSwitchStage,
  SpaceSwitchTransactionType,
} from "./spaceSwitchState";
import {
  abortSpaceSwitch,
  getActiveSpaceSwitchRunId,
  getActiveSpaceSwitchStage,
  getActiveSpaceSwitchTargetIndex,
  getActiveSpaceSwitchTransactionId,
  getActiveSpaceSwitchTransactionType,
  isActiveSpaceSwitchTransaction,
  isSpaceSwitchActive,
  setSpaceSwitchRestoreMode,
  setSpaceSwitchStage,
  setSpaceSwitchTargetIndex,
  softCompleteSpaceSwitch,
} from "./spaceSwitchState";
import { logEditorCorrelation } from "../../utils/editor";
import {
  dispatchSpaceNavigationEvent,
  SPACE_NAV_ABORTED_EVENT,
  SPACE_NAV_COMPLETED_EVENT,
  SPACE_NAV_INTERACTIVE_READY_EVENT,
  SPACE_NAV_RESTORE_READY_EVENT,
  SPACE_NAV_RUNTIME_RESTORED_EVENT,
  SPACE_NAV_SOFT_COMPLETED_EVENT,
  SPACE_NAV_SPACE_CONFIRMED_EVENT,
  SPACE_NAV_STABILIZED_EVENT,
  SPACE_NAV_TRANSACTION_STARTED_EVENT,
  SPACE_NAV_VSE_MODEL_READY_EVENT,
} from "./spaceNavigationEvents";

type CompletionGateMissingReason =
  | "missing_runtime_restored"
  | "missing_space_confirmed"
  | "missing_frame_ready"
  | "missing_vse_model_ready";

export type SpaceNavigationCompletionGateState = {
  transactionId: string | null;
  runtimeRestored: boolean;
  spaceConfirmed: boolean;
  frameReady: boolean;
  vseModelReady: boolean;
  expectsVseModel: boolean;
  interactionReady: boolean;
  stabilized: boolean;
  targetSpaceIndex: number | null;
  currentSpaceIndex: number | null;
  stage: string | null;
  missingReasons: CompletionGateMissingReason[];
};

type CoordinatorState = {
  transactionId: string | null;
  transactionType: SpaceSwitchTransactionType | null;
  targetSpaceIndex: number | null;
  currentSpaceIndex: number | null;
  runtimeRestored: boolean;
  restoreMode: SpaceSwitchRestoreMode | null;
  spaceConfirmed: boolean;
  frameReady: boolean;
  interactionReady: boolean;
  vseModelReady: boolean;
  expectsVseModel: boolean;
  stabilized: boolean;
  softCompleted: boolean;
  canonicalStateSeen: boolean;
  unityCompletionReceived: boolean;
};

const state: CoordinatorState = {
  transactionId: null,
  transactionType: null,
  targetSpaceIndex: null,
  currentSpaceIndex: null,
  runtimeRestored: false,
  restoreMode: null,
  spaceConfirmed: false,
  frameReady: false,
  interactionReady: false,
  vseModelReady: false,
  expectsVseModel: true,
  stabilized: false,
  softCompleted: false,
  canonicalStateSeen: false,
  unityCompletionReceived: false,
};

let lastCompletionGateLogSignature: string | null = null;

const INTERACTIVE_READY_FALLBACK_ALLOWED_STAGES = new Set<string | null>([
  "restoring_project",
  "restore_ready",
  "awaiting_space_confirmation",
  "interaction_ready",
  "stabilizing",
]);

const INTERACTIVE_READY_EVENT_ALLOWED_STAGES = new Set<string | null>([
  "restore_ready",
  "selecting_space",
  "awaiting_space_confirmation",
  "interaction_ready",
]);

function getMissingCompletionGates(): CompletionGateMissingReason[] {
  const missing: CompletionGateMissingReason[] = [];
  if (!state.runtimeRestored) missing.push("missing_runtime_restored");
  if (!state.spaceConfirmed) missing.push("missing_space_confirmed");
  if (!state.frameReady) missing.push("missing_frame_ready");
  if (state.expectsVseModel && !state.vseModelReady) {
    missing.push("missing_vse_model_ready");
  }
  return missing;
}

function buildCompletionGateState(): SpaceNavigationCompletionGateState {
  return {
    transactionId: state.transactionId,
    runtimeRestored: state.runtimeRestored,
    spaceConfirmed: state.spaceConfirmed,
    frameReady: state.frameReady,
    vseModelReady: state.vseModelReady,
    expectsVseModel: state.expectsVseModel,
    interactionReady: state.interactionReady,
    stabilized: state.stabilized,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    stage: getActiveSpaceSwitchStage(),
    missingReasons: getMissingCompletionGates(),
  };
}

function logCompletionGateBlocked(source: string): void {
  if (process.env.NODE_ENV !== "development") return;
  const gate = buildCompletionGateState();
  if (gate.missingReasons.length === 0) return;

  const signature = [
    gate.transactionId ?? "none",
    source,
    gate.stage ?? "none",
    gate.missingReasons.join(","),
  ].join(":");
  if (signature === lastCompletionGateLogSignature) return;
  lastCompletionGateLogSignature = signature;

  console.debug("[SpaceNavigation] completion gate blocked", {
    source,
    ...gate,
  });
}

/** Payload에 VSE 정보가 없을 때의 기본 추론 — space_add는 빈 Space가 일반적이라 true로 두면 VSE 완료를 무한 대기한다. */
function resolveHasLoadableVseWhenUnspecified(): boolean {
  return getActiveSpaceSwitchTransactionType() === "space_add" ? false : true;
}

function resetState(): void {
  state.transactionId = null;
  state.transactionType = null;
  state.targetSpaceIndex = null;
  state.currentSpaceIndex = null;
  state.runtimeRestored = false;
  state.restoreMode = null;
  state.spaceConfirmed = false;
  state.frameReady = false;
  state.interactionReady = false;
  state.vseModelReady = false;
  state.expectsVseModel = true;
  state.stabilized = false;
  state.softCompleted = false;
  state.canonicalStateSeen = false;
  state.unityCompletionReceived = false;
  lastCompletionGateLogSignature = null;
}

function resolveActiveTransactionId(
  transactionId?: string | null,
): string | null {
  return transactionId ?? getActiveSpaceSwitchTransactionId();
}

function matches(transactionId?: string | null): boolean {
  const resolved = resolveActiveTransactionId(transactionId);
  if (!resolved) return false;
  return (
    state.transactionId === resolved && isActiveSpaceSwitchTransaction(resolved)
  );
}

function maybeStabilize(source: string): void {
  const vseGateSatisfied = !state.expectsVseModel || state.vseModelReady;
  const hardCompletionAllowed =
    state.softCompleted || state.unityCompletionReceived;
  const isBlocked =
    !state.transactionId ||
    !matches(state.transactionId) ||
    state.stabilized ||
    !state.runtimeRestored ||
    !state.spaceConfirmed ||
    !state.frameReady ||
    !state.interactionReady ||
    !vseGateSatisfied ||
    !hardCompletionAllowed;

  if (isBlocked) {
    logCompletionGateBlocked(source);
    return;
  }

  const activeStage = getActiveSpaceSwitchStage();
  if (isSpaceSwitchActive()) {
    logEditorCorrelation(
      "space_switch_background_verification_deferred",
      {
        phase: "completed",
        source,
      },
      {
        transactionId: state.transactionId,
        targetSpaceIndex: state.targetSpaceIndex,
        currentSpaceIndex: state.currentSpaceIndex,
        stage: activeStage,
        owner: "spaceNavigationCoordinator",
        reason: "soft_completion_not_reached",
      },
    );
    return;
  }

  state.stabilized = true;
  // Match the existing Unity-terminal fast path when no soft completion was
  // delivered: keep interaction_ready terminal. Moving a still raw-active
  // switch to stabilizing would make it blocking again after UI completion.
  if (state.softCompleted) {
    setSpaceSwitchStage(state.transactionId, "stabilizing", { source });
    dispatchSpaceNavigationEvent(SPACE_NAV_STABILIZED_EVENT, {
      transactionId: state.transactionId,
      transactionType: state.transactionType,
      targetSpaceIndex: state.targetSpaceIndex,
      currentSpaceIndex: state.currentSpaceIndex,
      confirmedSpaceIndex: state.currentSpaceIndex,
      restoreMode: state.restoreMode,
      stage: "stabilizing",
      source,
    });
  }

  logEditorCorrelation(
    "space_switch_background_verified",
    {
      phase: "completed",
      source,
    },
    {
      transactionId: state.transactionId,
      runId: getActiveSpaceSwitchRunId(),
      targetSpaceIndex: state.targetSpaceIndex,
      currentSpaceIndex: state.currentSpaceIndex,
      owner: "spaceNavigationCoordinator",
      stage: getActiveSpaceSwitchStage(),
    },
  );
  dispatchSpaceNavigationEvent(SPACE_NAV_COMPLETED_EVENT, {
    transactionId: state.transactionId,
    transactionType: state.transactionType,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    confirmedSpaceIndex: state.currentSpaceIndex,
    restoreMode: state.restoreMode,
    stage: "completed",
    source,
    backgroundVerification: true,
  });
}

function getCurrentStageForEvent(fallback: SpaceSwitchStage): SpaceSwitchStage {
  return getActiveSpaceSwitchStage() ?? fallback;
}

export function getSpaceNavigationCompletionGateState(
  transactionId?: string | null,
): SpaceNavigationCompletionGateState | null {
  const resolvedTransactionId = resolveActiveTransactionId(transactionId);
  if (!resolvedTransactionId || !matches(resolvedTransactionId)) {
    return null;
  }
  return buildCompletionGateState();
}

export function resolveInteractiveReadyCompletionFallback(args?: {
  transactionId?: string | null;
  source?: string;
}): {
  status: "completed" | "ignored";
  gate: SpaceNavigationCompletionGateState | null;
} {
  const transactionId = resolveActiveTransactionId(args?.transactionId);
  const source = args?.source ?? "interactive_ready_completion_fallback";
  if (!transactionId || !matches(transactionId)) {
    return { status: "ignored", gate: null };
  }

  const gate = buildCompletionGateState();
  if (state.canonicalStateSeen) {
    logEditorCorrelation(
      "space_navigation_interactive_ready_fallback_suppressed",
      {
        phase: "interaction_ready",
        source,
      },
      {
        transactionId,
        targetSpaceIndex: state.targetSpaceIndex,
        currentSpaceIndex: state.currentSpaceIndex,
        owner: "spaceNavigationCoordinator",
        reason: "canonical_state_seen",
        stage: gate.stage,
      },
    );
    return { status: "ignored", gate };
  }

  if (state.stabilized || gate.missingReasons.length === 0) {
    maybeStabilize(source);
    return {
      status: state.stabilized ? "completed" : "ignored",
      gate: buildCompletionGateState(),
    };
  }

  if (!state.interactionReady) {
    return { status: "ignored", gate };
  }

  if (!INTERACTIVE_READY_FALLBACK_ALLOWED_STAGES.has(gate.stage)) {
    return { status: "ignored", gate };
  }

  logCompletionGateBlocked(source);
  return { status: "ignored", gate };
}

function maybeSoftCompleteProjectLoad(source: string): void {
  if (
    state.transactionType !== "project_load" ||
    state.softCompleted ||
    !state.transactionId
  ) {
    return;
  }

  // project_load는 transition overlay를 띄우지 않아(useSpaceTransitionOverlay early return)
  // overlay soft-completion fallback이 실행되지 않는다. interactive_ready 시점에
  // 명시적으로 soft complete하여 Editor UI lock(spaceSwitchUiLocked)이 해제되도록 한다.
  markSpaceNavigationSoftCompleted({
    transactionId: state.transactionId,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    reason: "interaction_ready_without_soft_completed",
    source: `spaceNavigationCoordinator:project_load:${source}`,
  });
}

function maybeDispatchInteractiveReady(source: string): void {
  if (
    !state.transactionId ||
    !matches(state.transactionId) ||
    state.interactionReady ||
    !state.runtimeRestored ||
    !state.spaceConfirmed ||
    !state.frameReady ||
    !INTERACTIVE_READY_EVENT_ALLOWED_STAGES.has(getActiveSpaceSwitchStage())
  ) {
    return;
  }

  state.interactionReady = true;
  setSpaceSwitchStage(state.transactionId, "interaction_ready", { source });
  dispatchSpaceNavigationEvent(SPACE_NAV_INTERACTIVE_READY_EVENT, {
    transactionId: state.transactionId,
    transactionType: state.transactionType,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    confirmedSpaceIndex: state.currentSpaceIndex,
    restoreMode: state.restoreMode,
    stage: "interaction_ready",
    source,
  });
  maybeSoftCompleteProjectLoad(source);
}

export function isSpaceNavigationTransitioning(): boolean {
  return isSpaceSwitchActive();
}

export function startSpaceNavigationTransaction(args: {
  transactionId: string;
  transactionType?: SpaceSwitchTransactionType;
  targetSpaceIndex?: number | null;
  source?: string;
}): void {
  const {
    transactionId,
    transactionType = getActiveSpaceSwitchTransactionType() ?? "space_switch",
    targetSpaceIndex = getActiveSpaceSwitchTargetIndex(),
    source = "unknown",
  } = args;

  state.transactionId = transactionId;
  state.transactionType = transactionType;
  state.targetSpaceIndex = targetSpaceIndex ?? null;
  state.currentSpaceIndex = null;
  state.runtimeRestored = false;
  state.restoreMode = null;
  state.spaceConfirmed = transactionType === "remount_only";
  state.frameReady = false;
  state.interactionReady = false;
  state.vseModelReady = transactionType === "remount_only";
  state.expectsVseModel = transactionType !== "remount_only";
  state.stabilized = false;
  state.softCompleted = false;
  state.canonicalStateSeen = false;
  state.unityCompletionReceived = false;
  setSpaceSwitchTargetIndex(targetSpaceIndex ?? null);

  const stage =
    transactionType === "space_switch"
      ? "pending_save"
      : transactionType === "space_add" || transactionType === "space_duplicate"
        ? "restore_ready"
        : transactionType === "project_load"
          ? "restoring_project"
          : "remounting";

  dispatchSpaceNavigationEvent(SPACE_NAV_TRANSACTION_STARTED_EVENT, {
    transactionId,
    transactionType,
    targetSpaceIndex: targetSpaceIndex ?? null,
    stage,
    source,
  });
}

export function markSpaceNavigationCanonicalStateSeen(detail?: {
  transactionId?: string | null;
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(detail?.transactionId);
  if (!transactionId || !matches(transactionId)) return;
  state.canonicalStateSeen = true;
}

export function markSpaceNavigationRuntimeRestored(args: {
  transactionId?: string | null;
  runId?: number;
  targetSpaceIndex?: number | null;
  currentSpaceIndex?: number | null;
  restoreMode: SpaceSwitchRestoreMode;
  hasLoadableVseInCurrentSpace?: boolean;
  loadableVseCountInCurrentSpace?: number;
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(args.transactionId);
  if (!transactionId || !matches(transactionId)) return;

  state.runtimeRestored = true;
  state.restoreMode = args.restoreMode;
  if (typeof args.targetSpaceIndex === "number") {
    state.targetSpaceIndex = args.targetSpaceIndex;
    setSpaceSwitchTargetIndex(args.targetSpaceIndex);
  }
  if (typeof args.currentSpaceIndex === "number") {
    state.currentSpaceIndex = args.currentSpaceIndex;
  }
  setSpaceSwitchRestoreMode(args.restoreMode);
  setSpaceSwitchStage(transactionId, "restore_ready", {
    runId: args.runId,
    restoreMode: args.restoreMode,
    currentSpaceIndex: args.currentSpaceIndex ?? null,
    source: args.source,
  });

  const explicitHasLoadable =
    typeof args.hasLoadableVseInCurrentSpace === "boolean"
      ? args.hasLoadableVseInCurrentSpace
      : typeof args.loadableVseCountInCurrentSpace === "number"
        ? args.loadableVseCountInCurrentSpace > 0
        : null;
  if (explicitHasLoadable !== null) {
    state.expectsVseModel = explicitHasLoadable;
    if (!explicitHasLoadable) {
      state.vseModelReady = true;
    }
  }

  dispatchSpaceNavigationEvent(SPACE_NAV_RUNTIME_RESTORED_EVENT, {
    transactionId,
    transactionType: state.transactionType,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    runId: args.runId,
    restoreMode: args.restoreMode,
    stage: "restore_ready",
    source: args.source,
  });
  dispatchSpaceNavigationEvent(SPACE_NAV_RESTORE_READY_EVENT, {
    transactionId,
    transactionType: state.transactionType,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    runId: args.runId,
    restoreMode: args.restoreMode,
    hasLoadableVseInCurrentSpace: args.hasLoadableVseInCurrentSpace,
    loadableVseCountInCurrentSpace: args.loadableVseCountInCurrentSpace,
    stage: "restore_ready",
    source: args.source,
  });

  maybeDispatchInteractiveReady(args.source ?? "runtime_restored");
  maybeStabilize(args.source ?? "runtime_restored");
}

export function markSpaceNavigationProjectReloaded(args: {
  transactionId?: string | null;
  runId?: number;
  targetSpaceIndex?: number | null;
  source?: string;
}): void {
  markSpaceNavigationRuntimeRestored({
    ...args,
    restoreMode: "reload",
  });
}

export function markSpaceNavigationSelecting(args: {
  transactionId?: string | null;
  targetSpaceIndex?: number | null;
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(args.transactionId);
  if (!transactionId || !matches(transactionId)) return;

  if (typeof args.targetSpaceIndex === "number") {
    state.targetSpaceIndex = args.targetSpaceIndex;
    setSpaceSwitchTargetIndex(args.targetSpaceIndex);
  }

  setSpaceSwitchStage(transactionId, "awaiting_space_confirmation", {
    source: args.source,
    targetSpaceIndex: state.targetSpaceIndex,
  });
}

export function confirmSpaceNavigation(detail: {
  transactionId?: string | null;
  targetSpaceIndex?: number | null;
  confirmedSpaceIndex?: number | null;
  currentSpaceIndex?: number | null;
  status?: string;
  hasLoadableVseInCurrentSpace?: boolean;
  loadableVseCountInCurrentSpace?: number;
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(detail.transactionId);
  if (!transactionId || !matches(transactionId)) return;

  const confirmedIndex =
    typeof detail.currentSpaceIndex === "number"
      ? detail.currentSpaceIndex
      : typeof detail.confirmedSpaceIndex === "number"
        ? detail.confirmedSpaceIndex
        : null;
  if (confirmedIndex === null) {
    return;
  }

  const requestedTargetIndex =
    typeof detail.targetSpaceIndex === "number"
      ? detail.targetSpaceIndex
      : state.targetSpaceIndex;

  state.spaceConfirmed = true;
  state.currentSpaceIndex = confirmedIndex;
  state.targetSpaceIndex = requestedTargetIndex ?? state.targetSpaceIndex;
  const hasLoadableVse =
    typeof detail.hasLoadableVseInCurrentSpace === "boolean"
      ? detail.hasLoadableVseInCurrentSpace
      : typeof detail.loadableVseCountInCurrentSpace === "number"
        ? detail.loadableVseCountInCurrentSpace > 0
        : resolveHasLoadableVseWhenUnspecified();
  state.expectsVseModel = hasLoadableVse;
  setSpaceSwitchTargetIndex(state.targetSpaceIndex ?? null);
  const eventStage = getCurrentStageForEvent("awaiting_space_confirmation");
  dispatchSpaceNavigationEvent(SPACE_NAV_SPACE_CONFIRMED_EVENT, {
    transactionId,
    transactionType: state.transactionType,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    confirmedSpaceIndex: confirmedIndex ?? null,
    hasLoadableVseInCurrentSpace: hasLoadableVse,
    loadableVseCountInCurrentSpace:
      typeof detail.loadableVseCountInCurrentSpace === "number"
        ? detail.loadableVseCountInCurrentSpace
        : hasLoadableVse
          ? undefined
          : 0,
    status: detail.status,
    stage: eventStage,
    source: detail.source,
  });

  maybeDispatchInteractiveReady(detail.source ?? "space_confirmed");
  maybeStabilize(detail.source ?? "space_confirmed");
}

export function markSpaceNavigationFrameReady(detail?: {
  transactionId?: string | null;
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(detail?.transactionId);
  if (!transactionId || !matches(transactionId)) return;
  if (state.stabilized || state.frameReady) return;
  state.frameReady = true;
  maybeDispatchInteractiveReady(detail?.source ?? "frame_ready");
  maybeStabilize(detail?.source ?? "frame_ready");
}

export function markSpaceNavigationVseModelReady(detail?: {
  transactionId?: string | null;
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(detail?.transactionId);
  if (!transactionId || !matches(transactionId)) return;

  // Unity가 실제 VSE를 올린 뒤 보내는 신호이다. canonical/휴리스틱으로 `expectsVseModel`이
  // 잘못 false이면 이미 stabilized인데도 VSE가 뒤늦게 로드되는 경우가 있어,
  // `stabilized || vseModelReady`에서 조기 return 하면 SPACE_NAV_VSE_MODEL_READY_EVENT가
  // 아예 안 나가 Entry Point(getList) 갱신이 영원히 빠질 수 있다 — 이벤트는 항상 내보낸다.
  if (!state.vseModelReady) {
    state.vseModelReady = true;
  }

  dispatchSpaceNavigationEvent(SPACE_NAV_VSE_MODEL_READY_EVENT, {
    transactionId,
    transactionType: state.transactionType,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    confirmedSpaceIndex: state.currentSpaceIndex,
    stage: getCurrentStageForEvent("awaiting_space_confirmation"),
    source: detail?.source ?? "vse_model_ready",
  });
  maybeStabilize(detail?.source ?? "vse_model_ready");
}

export function markSpaceNavigationSoftCompleted(detail: {
  transactionId?: string | null;
  targetSpaceIndex?: number | null;
  targetSpaceId?: string | null;
  currentSpaceIndex?: number | null;
  runId?: number;
  reason:
    | "already_restored"
    | "already_selected_after_restore"
    | "unity_space_confirmed"
    | "interaction_ready_without_soft_completed"
    | "model_load_complete_without_soft_completed";
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(detail.transactionId);
  if (!transactionId || !isActiveSpaceSwitchTransaction(transactionId)) return;

  if (state.transactionId !== transactionId) {
    state.transactionId = transactionId;
    state.transactionType =
      getActiveSpaceSwitchTransactionType() ?? "space_switch";
    state.targetSpaceIndex =
      typeof detail.targetSpaceIndex === "number"
        ? detail.targetSpaceIndex
        : getActiveSpaceSwitchTargetIndex();
    state.currentSpaceIndex =
      typeof detail.currentSpaceIndex === "number"
        ? detail.currentSpaceIndex
        : null;
    state.restoreMode = null;
    state.runtimeRestored = true;
    state.spaceConfirmed = false;
    state.frameReady = false;
    state.vseModelReady = false;
    state.expectsVseModel = true;
    state.stabilized = false;
    state.softCompleted = false;
    state.canonicalStateSeen = false;
    state.unityCompletionReceived = false;
  }

  if (typeof detail.targetSpaceIndex === "number") {
    state.targetSpaceIndex = detail.targetSpaceIndex;
    setSpaceSwitchTargetIndex(detail.targetSpaceIndex);
  }
  if (typeof detail.currentSpaceIndex === "number") {
    state.currentSpaceIndex = detail.currentSpaceIndex;
  }
  state.interactionReady = true;
  state.softCompleted = true;
  setSpaceSwitchStage(transactionId, "interaction_ready", {
    source: detail.source ?? "soft_completion",
    reason: detail.reason,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
  });
  softCompleteSpaceSwitch(transactionId, {
    source: detail.source ?? "soft_completion",
    reason: detail.reason,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
  });
  logEditorCorrelation(
    "space_switch_soft_completed",
    {
      phase: "interaction_ready",
      source: detail.source ?? "soft_completion",
    },
    {
      transactionId,
      runId: detail.runId ?? getActiveSpaceSwitchRunId(),
      targetSpaceIndex: state.targetSpaceIndex,
      targetSpaceId: detail.targetSpaceId ?? null,
      currentSpaceIndex: state.currentSpaceIndex,
      reason: detail.reason,
      backgroundVerificationRequired: true,
      owner: "spaceManagement",
      stage: "interaction_ready",
    },
  );
  dispatchSpaceNavigationEvent(SPACE_NAV_SOFT_COMPLETED_EVENT, {
    transactionId,
    transactionType: state.transactionType,
    targetSpaceIndex: state.targetSpaceIndex,
    targetSpaceId: detail.targetSpaceId ?? null,
    currentSpaceIndex: state.currentSpaceIndex,
    confirmedSpaceIndex:
      typeof detail.currentSpaceIndex === "number"
        ? detail.currentSpaceIndex
        : state.currentSpaceIndex,
    runId: detail.runId,
    restoreMode: state.restoreMode,
    stage: "interaction_ready",
    reason: detail.reason,
    backgroundVerificationRequired: true,
    source: detail.source ?? "soft_completion",
  });
  maybeStabilize(detail.source ?? "soft_completion");
}

export function completeSpaceNavigationFromUnity(detail: {
  transactionId?: string | null;
  runId?: number;
  targetSpaceIndex?: number | null;
  currentSpaceIndex?: number | null;
  hasLoadableVseInCurrentSpace?: boolean;
  loadableVseCountInCurrentSpace?: number;
  status?: string;
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(detail.transactionId);
  if (!transactionId || !matches(transactionId)) return;
  if (state.stabilized) return;

  const confirmedIndex =
    typeof detail.currentSpaceIndex === "number"
      ? detail.currentSpaceIndex
      : typeof detail.targetSpaceIndex === "number"
        ? detail.targetSpaceIndex
        : state.targetSpaceIndex;
  const hasLoadableVse =
    typeof detail.hasLoadableVseInCurrentSpace === "boolean"
      ? detail.hasLoadableVseInCurrentSpace
      : typeof detail.loadableVseCountInCurrentSpace === "number"
        ? detail.loadableVseCountInCurrentSpace > 0
        : resolveHasLoadableVseWhenUnspecified();

  const activeStage = getActiveSpaceSwitchStage();
  // Unity owns the accepted terminal facts. Actual frame/interaction readiness
  // and soft-completion delivery remain with their existing React/XR owners.
  // Retain the terminal so a later frame callback can finish this transaction.
  state.unityCompletionReceived = true;
  const isBackgroundVerification =
    state.frameReady &&
    state.interactionReady &&
    (!isSpaceSwitchActive() || activeStage === "interaction_ready");

  if (isBackgroundVerification) {
    state.runtimeRestored = true;
    state.spaceConfirmed = true;
    state.vseModelReady = true;
    state.expectsVseModel = hasLoadableVse;
    state.currentSpaceIndex = confirmedIndex ?? null;
    state.targetSpaceIndex =
      typeof detail.targetSpaceIndex === "number"
        ? detail.targetSpaceIndex
        : (state.targetSpaceIndex ?? confirmedIndex ?? null);
    state.stabilized = true;
    setSpaceSwitchTargetIndex(state.targetSpaceIndex ?? confirmedIndex ?? null);
    logEditorCorrelation(
      "space_switch_background_verified",
      {
        phase: "completed",
        source: detail.source ?? "completed_from_unity",
      },
      {
        transactionId,
        runId: detail.runId ?? getActiveSpaceSwitchRunId(),
        targetSpaceIndex: state.targetSpaceIndex,
        currentSpaceIndex: state.currentSpaceIndex,
        confirmedIndex,
        hasLoadableVseInCurrentSpace: hasLoadableVse,
        status: detail.status,
        owner: "unity",
        stage: activeStage,
      },
    );
    dispatchSpaceNavigationEvent(SPACE_NAV_COMPLETED_EVENT, {
      transactionId,
      transactionType: state.transactionType,
      targetSpaceIndex: state.targetSpaceIndex,
      currentSpaceIndex: state.currentSpaceIndex,
      confirmedSpaceIndex: confirmedIndex ?? null,
      restoreMode: state.restoreMode,
      hasLoadableVseInCurrentSpace: hasLoadableVse,
      loadableVseCountInCurrentSpace:
        typeof detail.loadableVseCountInCurrentSpace === "number"
          ? detail.loadableVseCountInCurrentSpace
          : hasLoadableVse
            ? undefined
            : 0,
      status: detail.status,
      stage: "completed",
      source: detail.source ?? "completed_from_unity",
      backgroundVerification: true,
    });
    return;
  }

  state.runtimeRestored = true;
  state.spaceConfirmed = true;
  state.vseModelReady = true;
  state.expectsVseModel = hasLoadableVse;
  state.currentSpaceIndex = confirmedIndex ?? null;
  state.targetSpaceIndex =
    typeof detail.targetSpaceIndex === "number"
      ? detail.targetSpaceIndex
      : (state.targetSpaceIndex ?? confirmedIndex ?? null);

  setSpaceSwitchTargetIndex(state.targetSpaceIndex ?? confirmedIndex ?? null);
  logEditorCorrelation(
    "space_switch_background_verification_deferred",
    {
      phase: "completed",
      source: detail.source ?? "completed_from_unity",
    },
    {
      transactionId,
      runId: detail.runId ?? getActiveSpaceSwitchRunId(),
      targetSpaceIndex: state.targetSpaceIndex,
      currentSpaceIndex: state.currentSpaceIndex,
      confirmedIndex,
      hasLoadableVseInCurrentSpace: hasLoadableVse,
      status: detail.status,
      owner: "unity",
      stage: activeStage,
      reason: "soft_completion_not_reached",
    },
  );
  maybeDispatchInteractiveReady(detail.source ?? "completed_from_unity");
  maybeStabilize(detail.source ?? "completed_from_unity");
}

export function abortSpaceNavigationTransaction(detail?: {
  transactionId?: string | null;
  reason?: string;
  source?: string;
}): void {
  const transactionId = resolveActiveTransactionId(detail?.transactionId);
  if (transactionId) {
    abortSpaceSwitch(transactionId, {
      reason: detail?.reason,
      source: detail?.source,
    });
  }

  dispatchSpaceNavigationEvent(SPACE_NAV_ABORTED_EVENT, {
    transactionId,
    transactionType: state.transactionType,
    targetSpaceIndex: state.targetSpaceIndex,
    currentSpaceIndex: state.currentSpaceIndex,
    reason: detail?.reason,
    stage: "aborted",
    source: detail?.source,
  });
  resetState();
}
